/**
 * HelpPage - Step-by-step instruction manual for clients
 * Designed for elderly users: large text, big touch targets, high contrast,
 * one-at-a-time accordion with numbered steps.
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Ticket,
  Users,
  XCircle,
  Clock,
  Gift,
  ChevronDown,
  ChevronLeft,
  AlertTriangle,
  Store,
  PlayCircle,
} from 'lucide-react';
import { OnboardingTour } from '@/components/onboarding/OnboardingTour';

interface GuideDefinition {
  key: string;
  icon: JSX.Element;
  stepCount: number;
  hasWarning: boolean;
  /** Capturas reales en public/help/ (regenerar con scripts/screenshots-manual/capture.mjs) */
  screenshots: string[];
}

const guideIconClass = 'w-7 h-7';

const GUIDES: GuideDefinition[] = [
  {
    key: 'book',
    icon: <Ticket className={guideIconClass} />,
    stepCount: 6,
    hasWarning: false,
    screenshots: ['paso-1-panel', 'paso-3-servicio', 'paso-4-barbero', 'paso-5-confirmar'],
  },
  {
    key: 'companions',
    icon: <Users className={guideIconClass} />,
    stepCount: 4,
    hasWarning: true,
    screenshots: ['paso-6-acompanantes'],
  },
  {
    key: 'cancel',
    icon: <XCircle className={guideIconClass} />,
    stepCount: 3,
    hasWarning: true,
    screenshots: ['paso-7-mis-turnos'],
  },
  { key: 'queue', icon: <Clock className={guideIconClass} />, stepCount: 4, hasWarning: false, screenshots: [] },
  { key: 'loyalty', icon: <Gift className={guideIconClass} />, stepCount: 4, hasWarning: false, screenshots: [] },
];

export default function HelpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openGuide, setOpenGuide] = useState<string | null>(GUIDES[0].key);
  const [showTour, setShowTour] = useState(false);
  // Refs per guide so opening one scrolls it to the top (read from the start).
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const toggleGuide = (key: string) => {
    setOpenGuide((current) => {
      const next = current === key ? null : key;
      if (next) {
        // Wait for the panel to expand, then bring its title to the top.
        requestAnimationFrame(() => {
          sectionRefs.current[next]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      return next;
    });
  };

  return (
    // Forced dark theme: the help manual always renders in dark mode (high
    // contrast, easier on the eyes for elderly users) regardless of app theme.
    <div className="dark">
    <div className="min-h-screen bg-canvas transition-colors duration-300">
      <Helmet>
        <title>{t('help.seo.title')}</title>
      </Helmet>

      {/* Header */}
      <header className="bg-surface shadow-md border-b border-hairline">
        <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="relative flex items-center min-h-[44px]">
            {/* Back button: returns to wherever the user came from (e.g. mid-booking) */}
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1 min-h-[44px] px-3 py-2 -ml-2 rounded-xl text-accent hover:text-accent hover:bg-white/5 text-base font-semibold transition-colors"
            >
              <ChevronLeft className="w-6 h-6" aria-hidden="true" />
              <span>{t('common.back')}</span>
            </button>
            <h1 className="absolute left-1/2 -translate-x-1/2 text-2xl sm:text-3xl font-bold text-white whitespace-nowrap pointer-events-none">
              {t('help.title')}
            </h1>
          </div>
          <p className="text-base text-ink-muted text-center mt-2">
            {t('help.subtitle')}
          </p>
        </div>
      </header>

      {/* Guides */}
      <main className="help-typeface container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-4">
          {/* Primary action: guided tutorial with screenshots (recommended for
              first-timers and elderly users — placed first so it's impossible to miss) */}
          <button
            type="button"
            onClick={() => setShowTour(true)}
            className="w-full min-h-[72px] rounded-2xl text-white shadow-lg px-5 py-4 flex items-center gap-4 text-left transition-colors"
          >
            <span
              className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-white/20"
              aria-hidden="true"
            >
              <PlayCircle className="w-7 h-7" />
            </span>
            <span className="flex-1">
              <span className="block text-xl font-bold">{t('onboarding.replay')}</span>
              <span className="block text-sm text-accent/90 mt-0.5">{t('help.tutorialHint')}</span>
            </span>
          </button>

          {GUIDES.map((guide) => {
            const isOpen = openGuide === guide.key;
            const buttonId = `help-guide-button-${guide.key}`;
            const panelId = `help-guide-panel-${guide.key}`;

            return (
              <section
                key={guide.key}
                ref={(el) => {
                  sectionRefs.current[guide.key] = el;
                }}
                className="bg-surface rounded-2xl border-2 border-hairline shadow-sm overflow-hidden scroll-mt-6"
              >
                <h2>
                  <button
                    id={buttonId}
                    type="button"
                    onClick={() => toggleGuide(guide.key)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    className={`w-full min-h-[64px] flex items-center gap-4 px-5 py-4 text-left transition-colors ${
                      isOpen
                        ? 'bg-accent/10'
                        : 'hover:bg-surface-2'
                    }`}
                  >
                    <span
                      className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full ${
                        isOpen
                          ? 'bg-accent/100 text-on-accent'
                          : 'bg-surface-2 text-accent'
                      }`}
                      aria-hidden="true"
                    >
                      {guide.icon}
                    </span>
                    <span className="flex-1 text-xl font-bold text-ink">
                      {t(`help.guides.${guide.key}.title`)}
                    </span>
                    <ChevronDown
                      className={`w-7 h-7 flex-shrink-0 text-ink-subtle transition-transform ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                </h2>

                {isOpen && (
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    className="px-5 pb-6 pt-2 border-t border-hairline"
                  >
                    <ol className="space-y-5 mt-4">
                      {Array.from({ length: guide.stepCount }, (_, index) => {
                        const stepNumber = index + 1;
                        return (
                          <li key={stepNumber} className="flex items-start gap-4">
                            <span
                              className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-accent/100 text-on-accent text-xl font-bold"
                              aria-hidden="true"
                            >
                              {stepNumber}
                            </span>
                            <div className="pt-1">
                              <h3 className="text-lg font-semibold text-ink">
                                {t(`help.guides.${guide.key}.steps.${stepNumber}.title`)}
                              </h3>
                              <p className="text-base text-ink-muted mt-1">
                                {t(`help.guides.${guide.key}.steps.${stepNumber}.description`)}
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ol>

                    {guide.screenshots.length > 0 && (
                      <div className="mt-6">
                        <h3 className="text-lg font-semibold text-ink mb-3">
                          {t('help.screenshotsTitle')}
                        </h3>
                        {/* Phone-style frames, consistent with the onboarding tour.
                            Full screenshot shown (no cropping) so nothing is lost in the manual. */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 justify-items-center">
                          {guide.screenshots.map((name, index) => (
                            <div
                              key={name}
                              className="w-full max-w-[200px] rounded-2xl border-4 border-hairline bg-surface shadow-md overflow-hidden"
                            >
                              <img
                                src={`/help/${name}.png`}
                                alt={t('help.screenshotAlt', {
                                  guide: t(`help.guides.${guide.key}.title`),
                                  number: index + 1,
                                })}
                                loading="lazy"
                                className="w-full h-auto block"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {guide.hasWarning && (
                      <div className="mt-6 bg-accent/10 border-2 border-accent/30 rounded-xl p-4 flex items-start gap-3">
                        <AlertTriangle
                          className="w-7 h-7 text-accent flex-shrink-0"
                          aria-hidden="true"
                        />
                        <p className="text-base text-accent">
                          <strong>{t('help.warningLabel')}</strong>{' '}
                          {t(`help.guides.${guide.key}.warning`)}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </section>
            );
          })}

          {/* Still need help card */}
          <section className="bg-surface rounded-2xl border-2 border-accent/30 shadow-sm p-6 flex items-start gap-4">
            <span
              className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-accent/100 text-on-accent"
              aria-hidden="true"
            >
              <Store className="w-7 h-7" />
            </span>
            <div>
              <h2 className="text-xl font-bold text-ink">
                {t('help.stillNeedHelp.title')}
              </h2>
              <p className="text-base text-ink-muted mt-1">
                {t('help.stillNeedHelp.description')}
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Onboarding tour replay (local state, does not touch first-use flag) */}
      <OnboardingTour isOpen={showTour} onClose={() => setShowTour(false)} />
    </div>
    </div>
  );
}
